import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import LinearProgress from '@mui/material/LinearProgress'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import {
  useInactivateAccountMutation,
  useInactivateEmailMutation,
  useChangeLoginMutation,
} from './useInactivatorMutations'

/**
 * Port of `app/inactivator/inactivator.html` +
 * `app/inactivator/inactivatorController.js` (AngularJS
 * `inactivatorController`) — the "Inativador" screen reachable at
 * `/workspace/inactivator` (legacy state `workspace.inactivator`).
 *
 * Three independent forms, exactly matching the legacy template's three
 * `<div class="row">` sections in order:
 * 1. "Inativador de conta" — tenant id -> `inactiveAccount(tenant)`.
 * 2. "Inativador de email" — email -> `inactiveEmail(email)`.
 * 3. "Ajuste de Trials - Aguardando Validação de Email" — old/new email ->
 *    `changeLogin(oldEmail, newEmail)`.
 *
 * The legacy template has no client-side validation on any of the three
 * plain `<input type="text">` fields (no `required`, no format check) and
 * the buttons are always enabled — ported as-is, the only "disabled" state
 * added here is while a request for that specific form is in flight, so a
 * user can't double-submit the same action (not a legacy behavior change,
 * just what MUI's `loading`-adjacent pattern makes easy and doesn't regress
 * anything the legacy screen relied on for its next enabled state).
 *
 * `$scope.loading` (legacy): a single flag shared by all three actions,
 * driving `#fountainG`'s indeterminate `.progress` bar and set back to
 * `false` in both the `.then` and `.catch` of whichever action ran. Ported
 * as a single shared `isPending` derived from the three mutations' own
 * `isPending` flags (TanStack Query's per-mutation loading state), rendered
 * as an MUI `LinearProgress` in the same place (top of the page, above the
 * first form) — functionally equivalent, no per-form spinners were invented
 * since the legacy screen had none either.
 *
 * Feedback: legacy used `Materialize.toast(...)` for both success (fixed
 * Portuguese message per action) and error (`error.data.errorMessage` from
 * the failed response) cases. Ported to a local `Snackbar` + `Alert`, same
 * precedent as `SettingsPage.jsx` (task 013) — no global toast/notistack
 * infra exists yet elsewhere in the app.
 */

const SUCCESS_MESSAGES = {
  account: 'Conta inativada com sucesso',
  email: 'Email inativado com sucesso',
  // The legacy `changeLogin` success toast reuses the *email* message
  // verbatim ("Email inativado com sucesso") even though nothing was
  // inactivated — confirmed by reading `inactivatorController.js` (no
  // separate message was ever written for this action). Kept as-is; this
  // looks like a copy-paste leftover in the legacy app but changing the
  // copy is a product decision out of scope for a 1:1 port.
  changeLogin: 'Email inativado com sucesso',
}

const GENERIC_ERROR_MESSAGE = 'Ocorreu um erro ao processar a solicitação.'

/** Port of `error.data.errorMessage` read off a rejected legacy `$http` response. */
function getErrorMessage(error) {
  return error?.response?.data?.errorMessage || GENERIC_ERROR_MESSAGE
}

function InactivatorPage() {
  const [tenant, setTenant] = useState('')
  const [email, setEmail] = useState('')
  const [oldEmail, setOldEmail] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' })

  const inactivateAccount = useInactivateAccountMutation()
  const inactivateEmail = useInactivateEmailMutation()
  const changeLogin = useChangeLoginMutation()

  const isPending = inactivateAccount.isPending || inactivateEmail.isPending || changeLogin.isPending

  function showSuccess(message) {
    setSnackbar({ open: true, severity: 'success', message })
  }

  function showError(error) {
    setSnackbar({ open: true, severity: 'error', message: getErrorMessage(error) })
  }

  function handleInactivateAccount() {
    inactivateAccount.mutate(tenant.trim(), {
      onSuccess: () => showSuccess(SUCCESS_MESSAGES.account),
      onError: (error) => showError(error),
    })
  }

  function handleInactivateEmail() {
    inactivateEmail.mutate(email.trim(), {
      onSuccess: () => showSuccess(SUCCESS_MESSAGES.email),
      onError: (error) => showError(error),
    })
  }

  function handleChangeLogin() {
    changeLogin.mutate(
      { oldEmail: oldEmail.trim(), newEmail: newEmail.trim() },
      {
        onSuccess: () => showSuccess(SUCCESS_MESSAGES.changeLogin),
        onError: (error) => showError(error),
      },
    )
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      {isPending && <LinearProgress sx={{ mb: 2 }} />}

      <Typography variant="h5" component="h2" gutterBottom>
        Inativador de conta
      </Typography>
      <Paper sx={{ p: 3, mb: 4 }} variant="outlined">
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <TextField
            id="tenant"
            label="Digite um tenant"
            value={tenant}
            onChange={(event) => setTenant(event.target.value)}
            fullWidth
            disabled={isPending}
          />
          <Button
            variant="contained"
            onClick={handleInactivateAccount}
            disabled={isPending}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Inativar
          </Button>
        </Stack>
      </Paper>

      <Typography variant="h5" component="h2" gutterBottom>
        Inativador de email
      </Typography>
      <Paper sx={{ p: 3, mb: 4 }} variant="outlined">
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <TextField
            id="email"
            label="Digite um email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
            disabled={isPending}
          />
          <Button
            variant="contained"
            onClick={handleInactivateEmail}
            disabled={isPending}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Inativar
          </Button>
        </Stack>
      </Paper>

      <Typography variant="h5" component="h2" gutterBottom>
        Ajuste de Trials - Aguardando Validação de Email
      </Typography>
      <Paper sx={{ p: 3 }} variant="outlined">
        <Stack spacing={2}>
          <TextField
            id="oldEmail"
            label="Digite o email atual"
            value={oldEmail}
            onChange={(event) => setOldEmail(event.target.value)}
            fullWidth
            disabled={isPending}
          />
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <TextField
              id="newEmail"
              label="Digite o novo email"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              fullWidth
              disabled={isPending}
            />
            <Button
              variant="contained"
              onClick={handleChangeLogin}
              disabled={isPending}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Trocar e-mail
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default InactivatorPage
